import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/locations.slice'; // Import your action creators
import * as api from '@/services/location'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { IPagination } from '../types';
import { IFetchLocation } from '@/services/location/location.type';
import { isSameRequestParams } from '@/utils/requestParamsChecker';

// Define action types
export interface GetLocationAction extends PayloadAction<number> {
  type: 'location/getLocation';
}

export interface GetLocationsAction extends PayloadAction<ILimit> {
  type: 'location/getLocations';
}

export interface CreateLocationAction extends PayloadAction<ICreateLocation> {
  type: 'location/createLocation';
}

export interface UpdateLocationAction extends PayloadAction<IUpdateLocation> {
  type: 'location/updateLocation';
}

export interface DeleteLocationAction extends PayloadAction<IDeleteLocation> {
  type: 'location/deleteLocation';
}

export interface GetLocationsListAction extends PayloadAction<number> {
  type: "location/getLocationsList";
}

// Define the shape of the API response for a single provider
export interface SingleLocationResponse {
  locationId: string;
  sourceLocationId: string;
  operatorId: string;
  name?: string;
  createdOn?: string;
  updatedOn?: string;
  // ... other properties
}
export interface ILocation {
  locationId: string;
  sourceLocationId: string;
  operatorId: string;
  name: string;
  createdOn?: string;
  updatedOn?: string;
}

export interface ICreateLocation {
  sourceLocationId: string;
  operatorId: string;
  locationName: string;
}

export interface IUpdateLocation {
  locationId?: string;
  sourceLocationId: string;
  locationName: string;
}

export interface IDeleteLocation {
  locationId: string;
}


export interface ApiResponse {
  success: boolean;
  message: string;
  data: ILocation[];
  pagination: IPagination;
}

export interface ILimit {
  page?: number;
  limit?: number;
  operatorId?: string
}

function* getLoactions(action: GetLocationsAction): Generator {
  try {
    const response: any = yield call(api?.fetchLocations as any, action?.payload);
    if (isValidLocationsResponse(response?.data)) {
      yield put(actions.getLocationsSuccess(response?.data));
    } else {
      yield put(actions.getLocationsFailure());
    }
  } catch (error) {
    yield put(actions.getLocationsFailure());
  }
}

// Your saga function
function* getLocation(action: GetLocationAction): Generator {
  try {
    const response: any = yield call(api.fetchLocation, action?.payload);
    const res = response?.data?.data

    if (isValidLocationResponse(res)) {
      yield put({
        type: 'location/getLocationSuccess',
        payload: res as SingleLocationResponse,
      });
    } else {
      yield put({ type: 'location/getLocationFailure' });
    }
  } catch (error) {
    yield put({ type: 'location/getLocationFailure' });
  }
}

function* createLocation(action: CreateLocationAction) {
  try {
    const response: unknown = yield call(api.createLocation as any, action);
    if (isValidLocationResponse(response)) {
      yield put({
        type: 'location/createLocationSuccess',
        payload: response as SingleLocationResponse,
      });
    } else {
      yield put({ type: 'location/createLocationFailure' });
    }
  } catch (error) {
    yield put({ type: 'location/createLocationFailure' });
  }
}

function* updateLocation(action: UpdateLocationAction) {
  try {
    const response: unknown = yield call(
      api.updateLocation as any,
      action?.payload?.locationId,
      action?.payload,
    );
    if (isValidLocationResponse(response)) {
      yield put({
        type: 'location/updateLocationSuccess',
        payload: response as SingleLocationResponse,
      });
    } else {
      yield put({ type: 'location/updateLocationFailure', payload: response });
    }
  } catch (error) {
    yield put({ type: 'location/updateLocationFailure', payload: "Something went wrong" });
  }
}

function* deleteLocation(action: DeleteLocationAction) {

  try {
    const response: unknown = yield call(api.deleteLocation, action?.payload);
    if (isValidLocationResponse(response)) {
      yield put({
        type: 'user/deleteUserSuccess',
        payload: response as SingleLocationResponse,
      });
    } else {
      yield put({ type: 'location/deleteLocationFailure' });
    }
  } catch (error) {
    yield put({ type: 'location/deleteLocationFailure', payload: "Something went wrong" });
  }
}

function isValidLocationResponse(
  response: unknown,
): response is SingleLocationResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'locationId' in response &&
    'sourceLocationId' in response &&
    'operatorId' in response &&
    'name' in response &&
    'createdOn' in response &&
    'updatedOn' in response
    // Add other properties if present in the API response
  );
}

function isValidLocationsResponse(response: ApiResponse): boolean {
  return (
    isBoolean(response.success) &&
    response.message === 'Locations found.'
  );
}

function* getLocationsList(action: PayloadAction<IFetchLocation>): Generator {
  try {
    if (isSameRequestParams(action.payload)) {
      return;
    }
    const response: any = yield call(api.fetchAllLocationsList, action.payload);
    const { operatorId, page, limit } = action.payload;
    if (isValidLocationsResponse(response?.data) || response?.data?.success) {
      yield put(actions.getLocationsListSuccess({
        ...response?.data,
        operatorId: operatorId, // <-- manually pass operatorId
      }));
    } else {
      yield put(actions.getLocationsListFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getLocationsListFailure(error?.response?.data));
  }
}

function* locationSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetLocationsAction>('location/getLocations', getLoactions);
  yield takeEvery<GetLocationAction>('location/getLocation', getLocation);
  yield takeLatest(actions.getLocationsList.type, getLocationsList);
  yield takeEvery<CreateLocationAction>(
    'location/createLocation',
    createLocation,
  );
  yield takeEvery<UpdateLocationAction>(
    'location/updateLocation',
    updateLocation,
  );
  yield takeEvery<DeleteLocationAction>(
    'location/deleteLocation',
    deleteLocation,
  );
}

export default locationSaga;
